import React, { useMemo, useState } from 'react';
import { Tree } from 'react-arborist';
import { 
  ChevronRightIcon, ChevronDownIcon, Box, Eye, Sun, Type, 
  Plus, Lock, Unlock, EyeOff, MoreVertical 
} from 'lucide-react';
import { useIDEStore } from '../../stores/ideStore';

interface TreeNode {
  id: string;
  name: string;
  children?: TreeNode[];
  type: string;
  visible?: boolean;
  locked?: boolean;
}

const getIconForType = (type: string) => {
  switch (type) {
    case 'Camera': return <Eye size={16} className="text-blue-400" />;
    case 'Light': return <Sun size={16} className="text-yellow-400" />;
    case 'Text': return <Type size={16} />;
    default: return <Box size={16} className="text-gray-400" />;
  }
};

export function HierarchyPanel({ className }: { className?: string }) {
  const { gameObjects, setSelectedObject, selectedObjectId, addGameObject, updateGameObject } = useIDEStore();
  const [showAddMenu, setShowAddMenu] = useState(false);

  const treeData = useMemo(() => {
    const objects = JSON.parse(JSON.stringify(gameObjects));
    const objectMap = new Map();
    const roots: TreeNode[] = [];

    objects.forEach((obj: any) => {
      objectMap.set(obj.id, { ...obj, children: [] });
    });

    objects.forEach((obj: any) => {
      const node = objectMap.get(obj.id);
      if (obj.parent) {
        const parent = objectMap.get(obj.parent);
        if (parent) {
          parent.children.push(node);
        } else {
          roots.push(node);
        }
      } else {
        roots.push(node);
      }
    });

    return roots;
  }, [gameObjects]);

  const handleAddObject = (type: string) => {
    addGameObject({
      name: `New ${type}`,
      type: type,
      parent: selectedObjectId || null, // Add as child of selected, or root
      components: []
    });
    setShowAddMenu(false);
  };

  const handleToggleVisibility = (e: React.MouseEvent, node: any) => {
    e.stopPropagation();
    updateGameObject(node.id, { visible: !node.visible });
  };

  const handleToggleLock = (e: React.MouseEvent, node: any) => {
    e.stopPropagation();
    updateGameObject(node.id, { locked: !node.locked });
  };

  const onMove = ({ dragIds, parentId, index }: any) => {
    // Handle re-parenting
    dragIds.forEach((id: string) => {
      updateGameObject(id, { parent: parentId });
    });
  };

  return (
    <div className={`hierarchy-panel ${className || ''}`} onClick={() => setShowAddMenu(false)}>
      <div className="panel-header flex justify-between items-center">
        <h3>Hierarchy</h3>
        <div className="relative">
          <button 
            className="p-1 hover:bg-[#444] rounded"
            onClick={(e) => { e.stopPropagation(); setShowAddMenu(!showAddMenu); }}
          >
            <Plus size={16} />
          </button>
          
          {showAddMenu && (
            <div className="absolute right-0 top-6 bg-[#2a2a2a] border border-[#444] rounded shadow-xl w-40 z-50 py-1">
              <div className="px-3 py-1 text-[10px] text-gray-500 uppercase tracking-wider font-bold">Create</div>
              <button className="w-full text-left px-3 py-1.5 hover:bg-[#375eab] text-xs" onClick={() => handleAddObject('Empty')}>Create Empty</button>
              <button className="w-full text-left px-3 py-1.5 hover:bg-[#375eab] text-xs" onClick={() => handleAddObject('Cube')}>3D Object</button>
              <button className="w-full text-left px-3 py-1.5 hover:bg-[#375eab] text-xs" onClick={() => handleAddObject('Light')}>Light</button>
              <button className="w-full text-left px-3 py-1.5 hover:bg-[#375eab] text-xs" onClick={() => handleAddObject('Camera')}>Camera</button>
              <button className="w-full text-left px-3 py-1.5 hover:bg-[#375eab] text-xs" onClick={() => handleAddObject('UI')}>UI Canvas</button>
            </div>
          )}
        </div>
      </div>
      
      <div className="panel-content hierarchy-content relative">
        <Tree
          data={treeData}
          openByDefault={true}
          width="100%"
          height={400}
          indent={24}
          rowHeight={28}
          onMove={onMove}
          onSelect={(nodes) => {
            if (nodes.length > 0) {
              setSelectedObject(nodes[0].data.id);
            } else {
              setSelectedObject(null);
            }
          }}
        >
          {({ node, style, dragHandle }) => (
            <div 
              style={style} 
              ref={dragHandle} 
              className={`
                group flex items-center justify-between px-2 cursor-pointer text-xs
                ${node.data.id === selectedObjectId ? 'bg-[#375eab] text-white' : 'text-gray-300 hover:bg-[#2a2a2a]'}
              `}
              onClick={() => node.select()}
            >
              <div className="flex items-center gap-2 overflow-hidden">
                <span className="flex items-center justify-center w-4 h-4" onClick={(e) => {
                  e.stopPropagation();
                  node.toggle();
                }}>
                  {!node.isLeaf && (
                    node.isOpen ? <ChevronDownIcon size={12} /> : <ChevronRightIcon size={12} />
                  )}
                </span>
                
                {getIconForType(node.data.type)}
                <span className="truncate">{node.data.name}</span>
              </div>

              {/* Action Icons (Visible on hover or if state is active) */}
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={(e) => handleToggleLock(e, node.data)}>
                  {node.data.locked ? <Lock size={12} className="text-orange-400" /> : <Unlock size={12} className="text-gray-500" />}
                </button>
                <button onClick={(e) => handleToggleVisibility(e, node.data)}>
                  {node.data.visible === false ? <EyeOff size={12} className="text-gray-500" /> : <Eye size={12} />}
                </button>
              </div>
            </div>
          )}
        </Tree>
      </div>

      <style jsx>{`
        .hierarchy-content {
          padding: 0;
        }
        .panel-header h3 {
          font-size: 12px;
          font-weight: 600;
          color: #ccc;
        }
      `}</style>
    </div>
  );
}
